/**
 * useConversationSummary Hook
 * Custom hook for managing conversation summary functionality
 * Uses Chat context for state management
 */

import { useCallback } from 'react';
import { useChatContext } from '../components/Chat/Chat.hook';
import { tUseConversationSummaryReturn } from '../types/summary.types';

/**
 * Custom hook for conversation summary functionality
 * Provides a clean interface for components to interact with summary features
 *
 * @returns Object with summary state and functions
 */
export function useConversationSummary(): tUseConversationSummaryReturn {
  const {
    conversationSummary,
    isSummaryLoading,
    summaryError,
    summaryEligibility,
    generateSummary: generateSummaryContext,
    checkSummaryEligibility: checkEligibilityContext,
    clearSummary: clearSummaryContext,
    clearSummaryError: clearErrorContext,
    selectedChat,
  } = useChatContext();

  /**
   * Generates a summary for the specified conversation
   * If no conversationId is provided, uses the currently selected chat
   */
  const generateSummary = useCallback(
    async (conversationId?: string): Promise<void> => {
      const targetConversationId = conversationId || selectedChat?.id;

      if (!targetConversationId) {
        throw new Error('No conversation ID provided and no chat selected');
      }

      await generateSummaryContext(targetConversationId);
    },
    [generateSummaryContext, selectedChat?.id],
  );

  /**
   * Checks if the specified conversation is eligible for summary generation
   * If no conversationId is provided, uses the currently selected chat
   */
  const checkEligibility = useCallback(
    async (conversationId?: string): Promise<void> => {
      const targetConversationId = conversationId || selectedChat?.id;

      if (!targetConversationId) {
        throw new Error('No conversation ID provided and no chat selected');
      }

      await checkEligibilityContext(targetConversationId);
    },
    [checkEligibilityContext, selectedChat?.id],
  );

  /**
   * Clears the current summary and any associated error
   */
  const clearSummary = useCallback((): void => {
    clearSummaryContext();
  }, [clearSummaryContext]);

  /**
   * Clears only the summary error, keeping the summary data
   */
  const clearError = useCallback((): void => {
    clearErrorContext();
  }, [clearErrorContext]);

  return {
    summary: conversationSummary,
    isLoading: isSummaryLoading,
    error: summaryError,
    eligibility: summaryEligibility,
    generateSummary,
    checkEligibility,
    clearSummary,
    clearError,
  };
}

export default useConversationSummary;
